import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import Table from 'components/customTable';

import ZpFilter from '../../../components/zpFilter';
import { FILTER_PARAMS, FILTER_TYPES } from '../../../components/zpFilter/filterUtils';
import CommonService from '../../../services/commonService';
import { JOB_BUCKET_GROUP } from '../../../utils/constant';
import HorizontalBarChart from '../charts/horizontalBarChart';

const xAxisLabels = ['0%', '20%', '40%', '60%', '80%', '100%'];

const DurationComparison = ({
  jobTitle,
  durations,
  yearsAtCurrentJobContentBlurb,
  filterValueRange,
  filterLogEventCallback,
}) => {
  const yAxisLabels = durations.map(({ duration }) => {
    return duration === 'Less than 1 year' ? 'Less than one year' : duration;
  });
  const values = durations.map(({ durationPercent }) => durationPercent);
  const [compareJobTitle, setCompareJobTitle] = useState('');
  const [compareValues, setCompareValues] = useState([]);
  const tableData = (durations ?? []).map((x) => ({
    ...x,
    durationPercentDisplay: x.durationPercent + '%',
  }));

  const handleFilterUpdate = async (_, data) => {
    const { value = jobTitle } = data;
    const durationByTitle = value.originalSearch ? value.originalSearch : value;

    try {
      if (durationByTitle === jobTitle && isEmpty(compareValues)) {
        return;
      } else if (durationByTitle === jobTitle && !isEmpty(compareValues)) {
        setCompareJobTitle('');
        setCompareValues([]);
        return;
      }

      const newDurationByTitle = durationByTitle !== jobTitle ? durationByTitle : '';
      if (!newDurationByTitle.length) return;
      const jobTitles = [newDurationByTitle];
      const data = { jobTitles };
      const newDurations = await CommonService.fetchTitleDurationsData(data);
      const newValues = newDurations[0].map(({ durationPercent }) => durationPercent);
      setCompareJobTitle(jobTitles[0]);
      setCompareValues(newValues);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <section id="duration-comparison-section">
      <h3 className="cmpSectionh4 z-mb-24 text-capitalize">
        {`Average ${jobTitle?.toLowerCase?.()} tenure`}
      </h3>
      {yearsAtCurrentJobContentBlurb && yearsAtCurrentJobContentBlurb.content && (
        <div dangerouslySetInnerHTML={{ __html: yearsAtCurrentJobContentBlurb.content }} />
      )}
      <div className="mt-4">
        <ZpFilter
          onApply={handleFilterUpdate}
          onClear={handleFilterUpdate}
          type={FILTER_TYPES.SINGLE}
          param={FILTER_PARAMS.JOB_TITLE}
          buttonText={'Compare Jobs'}
          valueRange={filterValueRange}
          options={{
            logEvent: () =>
              filterLogEventCallback(JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_DURATION),
            autoSuggest: true,
          }}
        />
        <div className="mt-4">
          <HorizontalBarChart
            values={values}
            jobTitle={jobTitle}
            xAxisLabels={xAxisLabels}
            yAxisLabels={yAxisLabels}
            compareValues={compareValues}
            compareJobTitle={compareJobTitle}
            yAxisLabelsWidth="100px"
            tooltipKey="diveristy-duration-comparison"
          />
        </div>
        <div className="mb-0 z-mt-98">
          <Table
            columnConfig={{
              duration: {
                columnName: 'Number of years',
              },
              durationPercentDisplay: {
                columnName: 'Percentages',
                customSortByField: 'durationPercent',
                // defaultSortDirection: 'desc'
              },
            }}
            bodyData={tableData}
            defaultRowsDisplay={99}
            tableHeaderTitleContent={
              <h3 className="zp-title-h3 text-capitalize z-mb-24">{`${jobTitle} tenure statistics`}</h3>
            }
          />
        </div>
      </div>
    </section>
  );
};

DurationComparison.propTypes = {
  jobTitle: PropTypes.string.isRequired,
  durations: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default DurationComparison;
